import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
// ponytail: date-fns fica só pelo texto aproximado ("há cerca de 2 horas"), que o Intl não tem.
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DataTable from '@/components/DataTable';
import ConfirmDialog from '@/components/ConfirmDialog';
import WorkflowStatusFilter from '@/components/WorkflowStatusChips';
import { PageCover, PageBody, CoverButton, CoverSearch, SheetHeader, StatusDot, DOT } from '@/components/Page';
import { API_URL } from '@/contexts/AuthContext';
import { formatBRL, formatBRLCompacto, formatNumero } from '@/lib/format';

// Busca tudo uma vez e filtra aqui: os números da capa ficam estáveis enquanto o filtro muda.
// Os predicados são os mesmos dos contadores, então contagem e lista sempre batem.
const ESCOPOS = {
  never_dispatched: { label: 'Nunca enviados', tone: 'att', color: DOT.att, filtro: (p) => p.never_dispatched },
  retrying: { label: 'Em retentativa', tone: 'run', color: DOT.run, filtro: (p) => !p.tiny_retry_exhausted && !p.never_dispatched },
  exhausted: { label: 'Esgotados', tone: 'bad', color: DOT.bad, filtro: (p) => p.tiny_retry_exhausted },
};

const TIME_AGO = (iso) => {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return iso;
  }
};

const ErrorDetails = ({ row }) => {
  const p = row.original;
  const rawErr = p.tiny_error_raw;
  return (
    <div className="space-y-4 px-5 py-5">
      <div>
        <h3 className="mb-2 text-[12.5px] font-medium text-gray-500">Mensagem completa do erro</h3>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-border bg-card p-3 text-xs">
          {p.tiny_error || '(vazio)'}
        </pre>
      </div>
      {rawErr && Object.keys(rawErr).length > 0 && (
        <div>
          <h3 className="mb-2 text-[12.5px] font-medium text-gray-500">Resposta bruta do Tiny</h3>
          <pre className="max-h-72 overflow-x-auto rounded-lg border border-border bg-card p-3 text-xs">
            {JSON.stringify(rawErr, null, 2)}
          </pre>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
        {[
          ['Pagamento confirmado', TIME_AGO(p.payment_confirmed_at)],
          ['Última tentativa', TIME_AGO(p.tiny_retry_last_at || p.tiny_error_at)],
          ['Email do cliente', p.cliente_email || '—'],
          ['Consultor', p.created_by_name || '—'],
        ].map(([rotulo, valor]) => (
          <div key={rotulo} className="min-w-0">
            <dt className="text-[12.5px] text-gray-500">{rotulo}</dt>
            <dd className="truncate font-medium">{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const TinyErrors = () => {
  const [scope, setScope] = useState('all');
  const [busca, setBusca] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryDialog, setRetryDialog] = useState({ open: false, pedido: null });
  const [retrying, setRetrying] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pedidos/tiny-errors`, {
        params: { scope: 'all', limit: 500 },
      });
      setItems(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar pedidos');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const stats = useMemo(() => ({
    all: items.length,
    valor: items.reduce((s, p) => s + Number(p.total || 0), 0),
    never_dispatched: items.filter(ESCOPOS.never_dispatched.filtro).length,
    retrying: items.filter(ESCOPOS.retrying.filtro).length,
    exhausted: items.filter(ESCOPOS.exhausted.filtro).length,
  }), [items]);

  const visiveis = useMemo(
    () => (ESCOPOS[scope] ? items.filter(ESCOPOS[scope].filtro) : items),
    [items, scope],
  );

  const handleConfirmRetry = async () => {
    if (!retryDialog.pedido) return;
    setRetrying(true);
    try {
      const response = await axios.post(`${API_URL}/pedidos/${retryDialog.pedido.id}/retry-tiny`, {});
      toast.success(`Pedido enviado ao Tiny. Número: ${response.data?.numero_tiny || 'criado'}`);
      setRetryDialog({ open: false, pedido: null });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao reprocessar pedido');
    } finally {
      setRetrying(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'numero_pedido',
        header: 'Pedido',
        cell: ({ row }) => (
          <div>
            <p className="whitespace-nowrap font-medium">{row.original.numero_pedido || row.original.id?.slice(0, 8)}</p>
            <p className="text-[12.5px] text-gray-500">{row.original.created_by_name || 'Sistema'}</p>
          </div>
        ),
      },
      {
        accessorKey: 'cliente_nome',
        header: 'Cliente',
        cell: ({ row }) => (
          <div>
            <p className="font-medium">{row.original.cliente_nome || '—'}</p>
            <p className="max-w-[200px] truncate text-[12.5px] text-gray-500">{row.original.cliente_email || ''}</p>
          </div>
        ),
      },
      {
        accessorKey: 'total',
        header: 'Valor',
        cell: ({ row }) => <span className="font-medium tabular-nums">{formatBRL(row.original.total)}</span>,
      },
      {
        accessorKey: 'tiny_error',
        header: 'Erro ou situação',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.never_dispatched ? (
            <span className="text-[13px] text-gray-600 dark:text-gray-300">Pago e ainda não enviado ao Tiny. Use Reprocessar.</span>
          ) : (
            <p className="max-w-[280px] truncate text-[13px] text-red-700 dark:text-red-300" title={row.original.tiny_error}>
              {row.original.tiny_error || '(sem mensagem)'}
            </p>
          ),
      },
      {
        accessorKey: 'tiny_retry_count',
        header: 'Status',
        cell: ({ row }) => {
          const n = row.original.tiny_retry_count || 0;
          if (row.original.never_dispatched) return <StatusDot tone="att">Nunca enviado</StatusDot>;
          if (row.original.tiny_retry_exhausted) return <StatusDot tone="bad">Esgotado após {n}</StatusDot>;
          return <StatusDot tone="run">{n} {n === 1 ? 'tentativa' : 'tentativas'}</StatusDot>;
        },
      },
      {
        accessorKey: 'tiny_error_at',
        header: 'Quando',
        cell: ({ row }) => (
          <span className="text-[13px] text-gray-500">
            {TIME_AGO(row.original.tiny_error_at || row.original.payment_confirmed_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => row.toggleExpanded()} className="gap-1">
              {row.getIsExpanded() ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              <span className="hidden md:inline">Detalhes</span>
            </Button>
            <Button size="sm" onClick={() => setRetryDialog({ open: true, pedido: row.original })} className="gap-1">
              <RefreshCw className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Reprocessar</span>
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageCover
        title="Pedidos fora do Tiny"
        subtitle="Pagos e ainda sem número no Tiny: a criação falhou ou o webhook foi para outro lugar (n8n)."
        actions={
          <>
            <CoverSearch value={busca} onChange={setBusca} placeholder="Pedido, cliente ou erro" />
            <CoverButton onClick={fetchItems} disabled={loading}>
              <RefreshCw className={loading ? 'animate-spin' : ''} />
              Atualizar
            </CoverButton>
          </>
        }
        figures={[
          { value: `${formatNumero(stats.all)}${stats.all >= 500 ? '+' : ''}`, label: stats.all === 1 ? 'pedido travado' : 'pedidos travados' },
          { value: formatBRLCompacto(stats.valor), label: 'parados fora do Tiny' },
          { value: formatNumero(stats.never_dispatched), label: 'nunca enviados' },
          { value: formatNumero(stats.retrying), label: 'em retentativa' },
          { value: formatNumero(stats.exhausted), label: 'esgotados, precisam de você' },
        ]}
      />
      <PageBody>
        <Card className="overflow-hidden">
          <SheetHeader title={`Pedidos travados · ${formatNumero(visiveis.length)}`}>
            <WorkflowStatusFilter value={scope} onChange={setScope} counts={stats} config={ESCOPOS} />
          </SheetHeader>
          <DataTable
            columns={columns}
            data={visiveis}
            loading={loading}
            searchable={false}
            searchValue={busca}
            onSearchChange={setBusca}
            searchKeys={['numero_pedido', 'cliente_nome', 'cliente_email', 'tiny_error', 'created_by_name']}
            emptyMessage={busca || scope !== 'all' ? 'Nenhum pedido com esse filtro.' : 'Tudo certo, nenhum pedido pendente de envio ao Tiny.'}
            initialSorting={[{ id: 'tiny_error_at', desc: true }]}
            initialPageSize={25}
            renderSubComponent={ErrorDetails}
            getRowCanExpand={() => true}
          />
        </Card>
      </PageBody>

      <ConfirmDialog
        open={retryDialog.open}
        onOpenChange={(open) => (open ? null : setRetryDialog({ open: false, pedido: null }))}
        title="Reprocessar pedido no Tiny"
        description="Vamos tentar criar este pedido no Tiny novamente. Dando certo, os contadores de erro são limpos."
        confirmText="Reprocessar"
        onConfirm={handleConfirmRetry}
        loading={retrying}
        variant="info"
        icon={RefreshCw}
        details={
          retryDialog.pedido
            ? [
                { label: 'Pedido', value: retryDialog.pedido.numero_pedido || retryDialog.pedido.id?.slice(0, 8) },
                { label: 'Cliente', value: retryDialog.pedido.cliente_nome || '—' },
                { label: 'Valor', value: formatBRL(retryDialog.pedido.total) },
                { label: 'Tentativas anteriores', value: retryDialog.pedido.tiny_retry_count || 0 },
              ]
            : null
        }
      />
    </div>
  );
};

export default TinyErrors;
